// @react
import React, { useEffect, useState, ReactElement } from 'react'
// @types
import { GetUserQuery, GetUserQueryVariables } from 'lib/graphql/graphqlTypes'
// @graphQL
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Cookies from 'universal-cookie/es6'
import { differenceInDays, addDays } from 'date-fns'
// @components
import Grid from '@bit/atd.web.grid'
import Banner from '@bit/atd.web.banner'
import Button from '@bit/atd.web.button'
// @module components
import { RouteNamesExternal } from 'constants/routeNamesExternal'
import useResizeObserver from 'use-resize-observer'

const useStyles = makeStyles({
  containerOuter: {
    padding: '24px 0',
    '& > div': {
      borderColor: '#d70000',
    },
    '& > div > span svg': {
      height: '24px',
      width: '24px',
    },
  },
  container: {
    '& $header + $actions': {
      paddingTop: 16,
    },
  },
  header: {
    '& $titleText + $subTitleText': {
      marginTop: 4,
    },
  },
  titleText: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '20px',
  },
  subTitleText: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '18px',
  },
  actions: {
    '& a + a': {
      marginLeft: '12px',
    },
  },
  isHorizontal: {
    '& $actions': {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    '& $header + $actions': {
      paddingTop: 0,
    },
  },
})

type PropsType = {
  isHiddenComponent?: ReactElement
}

const Reminder = ({ isHiddenComponent }: PropsType): ReactElement => {
  /**********************
   * CONST
   **********************/

  const timeFrames = [60, 30, 14, 7] // number of days when we should show the reminder
  const COOKIES_NAME = 'membershipReminder'

  /**********************
   * REFS
   **********************/

  const { ref } = useResizeObserver<HTMLDivElement>({
    onResize: ({ width }) => {
      if (width && width > 840) {
        setIsHoritonztal(true)
      } else {
        setIsHoritonztal(false)
      }
    },
  })

  /**********************
   * STATE
   **********************/

  const [isHidden, setIsHidden] = useState(true)
  const [isHorizontal, setIsHoritonztal] = useState(false)

  /**********************
   * HOOKS
   **********************/

  const classes = useStyles()
  const cookies = new Cookies()

  /**********************
   * GQL HOOKS
   **********************/

  const { data: user } = useQuery<GetUserQuery, GetUserQueryVariables>(
    GET_USER,
    {
      fetchPolicy: 'cache-first',
    },
  )
  const currentMembership = user?.user?.membership.currentMembership
  const futureMembership = user?.user?.membership.futureMembership
  const hasNationalFutureMembership = !!futureMembership?.find((item) => {
    return item.recordTypeCode === 'NATIONAL'
  })

  useEffect(() => {
    const shouldShow =
      currentMembership?.autoRenew === false &&
      !hasNationalFutureMembership &&
      diff <= timeFrames[0] &&
      !cookies.get(COOKIES_NAME)
    setIsHidden(!shouldShow)
  }, [currentMembership])

  /**********************
   * FUNCTIONS
   **********************/

  const diff = differenceInDays(
    new Date(currentMembership?.cycleEndDate * 1000),
    new Date(),
  )

  const hide = () => {
    const nextFrame = timeFrames.find((t) => t < diff)
    setIsHidden(true)
    if (!nextFrame) return //if number of days 7 or less

    cookies.set(COOKIES_NAME, diff, {
      path: '/',
      expires: addDays(
        new Date(currentMembership?.cycleEndDate * 1000),
        nextFrame,
      ),
    })
  }

  const handleClick = () => {
    window.analytics.track('Renew Banner Clicked')
  }

  /**********************
   * RENDER
   **********************/

  if (!user) return <></>
  if (user.user?.membership?.isEnterpriseMember === true) return <></>

  return (
    <Grid container>
      <Grid item xs={12}>
        <div ref={ref} style={{ fontSize: 0 }}>
          {!isHidden ? (
            <div className={classes.containerOuter}>
              <Banner fullWidth variant="error" icon="alert">
                <Grid
                  container
                  className={clsx(classes.container, {
                    [classes.isHorizontal]: isHorizontal,
                  })}
                >
                  {/* Header */}
                  <Grid
                    item
                    xs={isHorizontal ? 8 : 12}
                    className={classes.header}
                  >
                    <div
                      className={classes.titleText}
                    >{`You have ${diff} days before your membership ends.`}</div>
                    <div className={classes.subTitleText}>
                      Make sure you renew now or you will lose access to your
                      benefits.
                    </div>
                  </Grid>

                  {/* Actions */}
                  <Grid
                    item
                    xs={isHorizontal ? 4 : 12}
                    className={classes.actions}
                  >
                    <Button
                      size={'small'}
                      onClick={handleClick}
                      href={RouteNamesExternal.CHECKOUT_MEMBERSHIP}
                    >
                      Renew Now
                    </Button>
                    {diff > 7 ? (
                      <Button
                        size={'small'}
                        variant={'tertiary'}
                        linkIcon="close"
                        onClick={hide}
                      >
                        Hide
                      </Button>
                    ) : (
                      <></>
                    )}
                  </Grid>
                </Grid>
              </Banner>
            </div>
          ) : (
            <>{isHiddenComponent ? isHiddenComponent : <></>}</>
          )}
        </div>
      </Grid>
    </Grid>
  )
}

export default Reminder
